export interface SandboxEnvironmentContext {
  mode: string;
  deploymentProfile: string;
  claimBoundary: string;
}

export interface SandboxOrganizationContext {
  id: string;
  label: string;
  tenantId: string;
  trustDomainId: string;
  roleSummary: string;
  supportReference: string;
}

export interface SandboxMembershipOption {
  id: string;
  label: string;
  selected: boolean;
}

export interface SandboxInviteState {
  recipient: string;
  roleBundle: string;
  status: string;
  expiresIn: string;
  supportReference: string;
}

export interface SandboxDeniedState {
  reasonCode: string;
  title: string;
  detail: string;
  remediation: string;
  supportReference: string;
}

export interface ExperienceContract {
  environment: SandboxEnvironmentContext;
  activeOrganization: SandboxOrganizationContext;
  memberships: SandboxMembershipOption[];
  invites: SandboxInviteState[];
  deniedState: SandboxDeniedState | null;
  grantedScopes: string[];
  grantedPermissions: string[];
  source: "api" | "fixture";
}

export const EXPERIENCE_CONTRACT_FIXTURE: ExperienceContract = {
  source: "fixture",
  environment: {
    mode: "sandbox",
    deploymentProfile: "sandbox-shared",
    claimBoundary: "Shared Alloy-hosted sandbox. Non-production, non-custodial, no live funds.",
  },
  activeOrganization: {
    id: "customer-a-org",
    label: "Customer A Sandbox",
    tenantId: "customer-a-tenant",
    trustDomainId: "customer-a-sandbox",
    roleSummary: "Organization Admin Starter",
    supportReference: "support-ref-org-selection-42",
  },
  memberships: [
    { id: "customer-a-org", label: "Customer A Sandbox", selected: true },
    { id: "blockrewards-customer-org", label: "BlockRewards Sandbox", selected: false },
    { id: "customer-b-org", label: "Customer B Sandbox", selected: false },
  ],
  invites: [
    {
      recipient: "maya.iyer@customer.example",
      roleBundle: "organization_admin_starter",
      status: "invited",
      expiresIn: "23h",
      supportReference: "support-ref-invite-17",
    },
    {
      recipient: "noah.patel@customer.example",
      roleBundle: "auditor",
      status: "mfa_pending",
      expiresIn: "18h",
      supportReference: "support-ref-invite-22",
    },
  ],
  deniedState: {
    reasonCode: "wallet_operation_role_denied",
    title: "Wallet operations are blocked for this role",
    detail: "This session can inspect setup state, but cannot approve, sign, broadcast, or mutate customer wallet authority.",
    remediation: "Ask an Organization Admin to assign a wallet role after admin quorum and role mapping readiness are complete.",
    supportReference: "support-ref-role-denied-09",
  },
  grantedScopes: [],
  grantedPermissions: [],
};

export function normalizeExperienceContract(input: unknown, source: "api" | "fixture" = "api"): ExperienceContract {
  const data = objectRecord(input) ?? {};
  const principal = objectRecord(data.principalContext) || objectRecord(data.context) || {};
  const environment = objectRecord(data.environment) || {};
  const active = objectRecord(data.activeOrganization) || objectRecord(data.activeMembership) || objectRecord(data.organization) || {};
  const fallback = EXPERIENCE_CONTRACT_FIXTURE;
  const activeOrganization = {
    id: boundedId(active.id || active.organizationId || principal.organizationId, fallback.activeOrganization.id),
    label: boundedText(active.label || active.displayName || active.organizationName, fallback.activeOrganization.label),
    tenantId: boundedId(active.tenantId || principal.tenantId, fallback.activeOrganization.tenantId),
    trustDomainId: boundedId(active.trustDomainId || principal.trustDomainId, fallback.activeOrganization.trustDomainId),
    roleSummary: boundedText(active.roleSummary || roleSummary(active.roles || data.roles), fallback.activeOrganization.roleSummary),
    supportReference: boundedText(active.supportReference || data.supportReference, fallback.activeOrganization.supportReference),
  };

  return {
    source,
    environment: {
      mode: boundedId(environment.mode || data.environmentMode || principal.environment || data.environment, fallback.environment.mode),
      deploymentProfile: boundedId(
        environment.deploymentProfile || data.deploymentProfile || principal.deploymentProfile,
        fallback.environment.deploymentProfile,
      ),
      claimBoundary: boundedText(environment.claimBoundary || data.claimBoundary, fallback.environment.claimBoundary),
    },
    activeOrganization,
    memberships: normalizeMemberships(data.memberships, activeOrganization),
    invites: normalizeInvites(data.invites),
    deniedState: normalizeDeniedState(data.deniedState || data.roleDenied),
    grantedScopes: normalizeStringList(data.grantedScopes),
    grantedPermissions: normalizeStringList(data.grantedPermissions),
  };
}

function normalizeMemberships(input: unknown, active: SandboxOrganizationContext): SandboxMembershipOption[] {
  const items = Array.isArray(input) ? input : EXPERIENCE_CONTRACT_FIXTURE.memberships;
  return items.slice(0, 6).map((raw) => {
    const item = objectRecord(raw) || {};
    const id = boundedId(item.id || item.organizationId, active.id);
    return {
      id,
      label: boundedText(item.label || item.displayName || item.organizationName, active.label),
      selected: item.selected === true || id === active.id,
    };
  });
}

function normalizeInvites(input: unknown): SandboxInviteState[] {
  const items = Array.isArray(input) ? input : EXPERIENCE_CONTRACT_FIXTURE.invites;
  return items.slice(0, 4).map((raw) => {
    const item = objectRecord(raw) || {};
    return {
      recipient: boundedText(item.recipient || item.email, "pending admin"),
      roleBundle: boundedText(item.roleBundle || item.role, "organization_admin_starter"),
      status: boundedId(item.status, "invited"),
      expiresIn: boundedText(item.expiresIn || item.expires_in, "24h"),
      supportReference: boundedText(item.supportReference, "support-ref-invite"),
    };
  });
}

function normalizeDeniedState(input: unknown): SandboxDeniedState | null {
  if (input == null) return null;
  const item = objectRecord(input);
  if (!item) return null;
  const reasonCode = typeof item.reason_code === "string" ? item.reason_code : typeof item.reasonCode === "string" ? item.reasonCode : "";
  if (!reasonCode.trim()) return null;
  return {
    reasonCode: boundedId(reasonCode, "unknown"),
    title: boundedText(item.title, "Access restricted"),
    detail: boundedText(item.detail, ""),
    remediation: boundedText(item.remediation, ""),
    supportReference: boundedText(item.supportReference, ""),
  };
}

function normalizeStringList(input: unknown): string[] {
  if (!Array.isArray(input)) return [];
  return input.filter((v): v is string => typeof v === "string" && v.trim().length > 0).slice(0, 100);
}

function roleSummary(input: unknown): string {
  return Array.isArray(input) ? input.slice(0, 3).map(String).join(", ") : "";
}

function objectRecord(input: unknown): Record<string, unknown> | null {
  return input && typeof input === "object" && !Array.isArray(input) ? input as Record<string, unknown> : null;
}

function boundedText(input: unknown, fallback: string): string {
  if (typeof input !== "string") return fallback;
  const value = input.trim();
  return value ? value.slice(0, 140) : fallback;
}

function boundedId(input: unknown, fallback: string): string {
  if (typeof input !== "string") return fallback;
  const value = input.trim();
  return /^[a-zA-Z0-9_.:-]{2,96}$/.test(value) ? value : fallback;
}
